import { useCallback, useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { Calendar, Clock, MapPin, Users, Euro, ArrowLeft, Zap } from "lucide-react";
import { api, formatApiError, formatDate, formatCompensation, URGENT_COST } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ApplicantRow, StatusBadge } from "@/components/ApplicantRow";
import { ReportButton } from "@/components/ReportButton";
import { UrgentBadge } from "@/components/UrgentBadge";

const UrgentAction = ({ ev, onDone }) => {
  const { user, refresh } = useAuth();
  const [loading, setLoading] = useState(false);
  if (ev.is_urgent) return <p className="text-xs text-neutral-400 mt-6" data-testid="event-urgent-active">Evento urgente: in cima alla bacheca fino al {formatDate(ev.date)}.</p>;
  if (ev.status !== "open" || ev.hidden || ev.date < new Date().toISOString().slice(0, 10)) return null;
  const enough = (user.credits ?? 0) >= URGENT_COST;
  const go = async () => {
    setLoading(true);
    try { await api.post(`/events/${ev.id}/urgent`); toast.success("Evento segnato come urgente"); refresh(); onDone(); }
    catch (e) { toast.error(formatApiError(e)); } finally { setLoading(false); }
  };
  return (
    <div className="mt-6 pt-6 border-t border-white/5">
      <button onClick={go} disabled={loading || !enough} className="btn-ghost w-full !border-[#E5484D]/50 !text-[#E5484D] hover:!bg-[#E5484D]/10 inline-flex items-center justify-center gap-2" data-testid="event-make-urgent-button">
        <Zap className="w-4 h-4" />{loading ? "Attivazione…" : `Rendi urgente · ${URGENT_COST} crediti`}
      </button>
      <p className="text-xs text-neutral-500 mt-2" data-testid="event-urgent-info">Badge "Urgente" e posizione in cima alla bacheca fino alla data dell'evento.{!enough && <> <Link to="/crediti" className="text-[#D4AF37] hover:underline" data-testid="event-urgent-buy-credits-link">Acquista crediti</Link></>}</p>
    </div>
  );
};

const Info = ({ icon: Icon, label, value }) => (
  <div className="py-4 border-b border-white/5 flex items-center gap-4">
    <Icon className="w-4 h-4 text-[#D4AF37]" />
    <span className="text-sm text-neutral-500 w-28">{label}</span>
    <span className="text-sm text-neutral-200">{value || "—"}</span>
  </div>
);

const ApplyBox = ({ ev, onApplied }) => {
  const [msg, setMsg] = useState("");
  const [loading, setLoading] = useState(false);
  if (ev.my_application) return (
    <div className="p-6 border border-[#D4AF37]/20 bg-[#D4AF37]/5" data-testid="event-my-application">
      <p className="eyebrow">La tua candidatura</p>
      <div className="mt-4"><StatusBadge status={ev.my_application.status} testid="event-my-application-status" /></div>
    </div>
  );
  if (ev.status !== "open") return <p className="text-neutral-500" data-testid="event-closed-msg">Questo evento non accetta più candidature.</p>;
  const apply = async () => {
    setLoading(true);
    try { await api.post(`/events/${ev.id}/apply`, { message: msg }); toast.success("Disponibilità inviata al cliente"); onApplied(); }
    catch (e) { toast.error(formatApiError(e)); } finally { setLoading(false); }
  };
  return (
    <div className="p-6 border border-white/10 bg-[#121418]" data-testid="event-apply-box">
      <p className="eyebrow">Sei disponibile?</p>
      <textarea rows={3} value={msg} onChange={(e) => setMsg(e.target.value)} placeholder="Messaggio per il cliente (opzionale)" className="lux-input resize-none mt-2" data-testid="event-apply-message" />
      <button onClick={apply} disabled={loading} className="btn-gold w-full mt-6" data-testid="event-apply-button">{loading ? "Invio…" : "Candidati ora"}</button>
    </div>
  );
};

export default function EventDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [ev, setEv] = useState(null);
  const load = useCallback(() => api.get(`/events/${id}`).then((r) => setEv(r.data)).catch(() => setEv(false)), [id]);
  useEffect(() => { load(); }, [load]);

  const decide = async (appId, status) => {
    try { await api.post(`/applications/${appId}/decision`, { status }); toast.success(status === "accepted" ? "Hostess selezionata" : "Candidatura rifiutata"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const toggle = async () => { await api.post(`/events/${id}/close`); load(); };

  if (ev === null) return <p className="eyebrow text-center py-40">Caricamento…</p>;
  if (!ev) return <p className="font-serif text-3xl text-center py-40" data-testid="event-not-found">Evento non trovato</p>;

  return (
    <main className="max-w-7xl mx-auto px-6 lg:px-10 pt-12" data-testid="event-detail-page">
      <Link to={ev.is_owner ? "/dashboard" : "/eventi"} className="text-xs uppercase tracking-[0.18em] text-neutral-500 hover:text-[#D4AF37] flex items-center gap-2" data-testid="event-back-link"><ArrowLeft className="w-3 h-3" /> Indietro</Link>
      <div className="grid lg:grid-cols-12 gap-12 mt-10">
        <section className="lg:col-span-7 fade-up">
          <div className="flex items-center gap-3 flex-wrap">
            <p className="eyebrow">{ev.category} · {ev.client_name}</p>
            {ev.is_urgent && <UrgentBadge testid="event-urgent-badge" />}
          </div>
          <h1 className="font-serif text-4xl sm:text-5xl mt-4 leading-tight" data-testid="event-title">{ev.title}</h1>
          {ev.hidden && <p className="mt-4 text-sm text-amber-400 border border-amber-400/30 bg-amber-400/5 px-4 py-3" data-testid="event-hidden-banner">Questo evento è stato nascosto dall'amministrazione perché non rispetta le regole: non è visibile alle hostess.</p>}
          {!ev.is_owner && <div className="mt-4"><ReportButton targetType="event" targetId={ev.id} label="Segnala evento" /></div>}
          <p className="text-neutral-300 mt-6 leading-relaxed whitespace-pre-line">{ev.description || "Nessuna descrizione."}</p>
          <div className="mt-10">
            <Info icon={MapPin} label="Luogo" value={[ev.city, ev.location].filter(Boolean).join(" · ")} />
            <Info icon={Calendar} label="Data" value={formatDate(ev.date)} />
            <Info icon={Clock} label="Orario" value={ev.start_time && `${ev.start_time} – ${ev.end_time}`} />
            <Info icon={Euro} label="Compenso" value={formatCompensation(ev)} />
            <Info icon={Users} label="Posti" value={`${ev.accepted_count} / ${ev.hostess_needed} selezionate`} />
          </div>
        </section>
        <aside className="lg:col-span-5">
          {user.role === "hostess" && <ApplyBox ev={ev} onApplied={load} />}
          {ev.is_owner && (
            <div className="p-6 border border-white/10 bg-[#121418]">
              <p className="eyebrow">Stato evento</p>
              <p className="font-serif text-3xl mt-3" data-testid="event-status">{ev.status === "open" ? "Aperto alle candidature" : "Chiuso"}</p>
              <button onClick={toggle} className="btn-ghost w-full mt-6" data-testid="event-toggle-status-button">{ev.status === "open" ? "Chiudi candidature" : "Riapri candidature"}</button>
              <UrgentAction ev={ev} onDone={load} />
            </div>
          )}
        </aside>
      </div>
      {ev.is_owner && (
        <section className="mt-20" data-testid="event-applicants-section">
          <div className="flex items-end justify-between">
            <h2 className="font-serif text-3xl">Hostess disponibili</h2>
            <span className="font-mono text-xs text-neutral-500" data-testid="event-applicants-count">{ev.applications.length} candidature</span>
          </div>
          <div className="space-y-3 mt-8">
            {ev.applications.length === 0 ? <p className="text-neutral-500 border border-white/5 p-10 text-center" data-testid="event-no-applicants">Ancora nessuna candidatura. Riceverai una notifica appena un'hostess sarà disponibile.</p>
              : ev.applications.map((a) => <ApplicantRow key={a.id} a={a} onDecide={decide} onUnlocked={load} />)}
          </div>
        </section>
      )}
    </main>
  );
}
